export type MarketplaceListing = {
  // `id` is a stable site identifier. Marketplace relists sometimes receive a new Facebook id.
  id: string;
  marketplaceId?: string;
  title: string;
  price: number;
  previousPrice?: number;
  location: string;
  availability?: 'available' | 'pending' | 'reserved' | 'sold' | 'hidden';
  listed?: string;
  dimensions?: string;
  // Width x height used by room preview and AR when Leslie's listing states dimensions in another order.
  roomDimensions?: string;
  imageSlug: string;
  marketplaceImage?: string;
  listingUrl: string;
  tags: string[];
  note?: string;
  soldNote?: string;
  media?: MarketplaceMedia[];
};

export type MarketplaceMedia = {
  type: 'image' | 'video';
  src: string;
  label: string;
  kind: 'artwork' | 'context' | 'framed' | 'detail' | 'video';
  poster?: string;
  note?: string;
};

const item = (id: string) =>
  `https://www.facebook.com/marketplace/item/${id}/?ref=marketplace_profile`;

const marketplacePhoto = (folder: string, index: number, framed: boolean) => {
  if (index === 0) {
    return `/art/facebook/${folder}/${folder}-original-lowcountry-painting-leslie-roberts-facebook-marketplace-full-view.jpg`;
  }

  const kind = framed ? 'framed' : 'context';
  return `/art/facebook/${folder}/${folder}-leslie-roberts-lowcountry-painting-facebook-marketplace-${kind}-view-${String(index + 1).padStart(2, '0')}.jpg`;
};

// Every secondary image below was captured from Leslie's live Marketplace listing.
// A framed label is used only when the listing says that the work is framed.
const marketplaceMedia = (folder: string, count: number, framed = false): MarketplaceMedia[] =>
  Array.from({ length: count }, (_, index) => ({
    type: 'image',
    src: marketplacePhoto(folder, index, framed),
    label: index === 0 ? 'Marketplace full artwork view' : framed ? `Marketplace framed view ${index + 1}` : `Marketplace context view ${index + 1}`,
    kind: index === 0 ? 'artwork' : framed ? 'framed' : 'context'
  }));

export const marketplaceListings: MarketplaceListing[] = [
  {
    id: '1684835732685659',
    marketplaceId: '1973045100069419',
    title: 'Roseate Spoonbill',
    price: 150,
    previousPrice: 225,
    location: 'Mt Pleasant, SC',
    dimensions: '24 x 18',
    roomDimensions: '18 x 24',
    imageSlug: 'roseate-spoonbill-perched-on-branch-leslie-roberts',
    listingUrl: item('1973045100069419'),
    tags: ['Wildlife', 'Coastal', 'Original'],
    note: 'Leslie has two 24 x 18 spoonbill originals available at $150 each or $250 for both.',
    media: marketplaceMedia('roseate-spoonbill', 5)
  },
  {
    id: '1987657718563301',
    title: 'Saltgrass Glow',
    price: 350,
    location: 'Mt Pleasant, SC',
    availability: 'hidden',
    dimensions: '24 x 18',
    roomDimensions: '18 x 24',
    imageSlug: 'saltmarsh-glow-sunset-over-marshland-leslie-roberts',
    marketplaceImage: '/art/facebook/saltgrass-glow/saltgrass-glow-original-lowcountry-marsh-painting-leslie-roberts-marketplace-full.jpg',
    listingUrl: item('1987657718563301'),
    tags: ['Golden hour', 'Marsh', 'Framed option', 'Marketplace views', 'Original'],
    note: 'This work was not shown in Leslie\'s active Marketplace listings when the catalog was checked on October 2, 2026.'
  },
  {
    id: '26832917529712415',
    title: 'Lowcountry Sunset Harbor',
    price: 325,
    location: 'Mt Pleasant, SC',
    availability: 'hidden',
    imageSlug: 'lowcountry-sunset-harbor-leslie-roberts',
    listingUrl: item('26832917529712415'),
    tags: ['Harbor', 'Sunset', 'Original'],
    note: 'This work was not shown in Leslie\'s active Marketplace listings when the catalog was checked on October 2, 2026.'
  },
  {
    id: '1287471063370672',
    marketplaceId: '1036005212387101',
    title: 'Dock on the Marsh',
    price: 75,
    location: 'Huger, SC',
    dimensions: '16 x 20',
    roomDimensions: '20 x 16',
    imageSlug: 'lowcountry-docks-leslie-roberts',
    listingUrl: item('1036005212387101'),
    tags: ['Dock', 'Small work', 'Framed', 'Original'],
    media: marketplaceMedia('dock-on-the-marsh', 4, true)
  },
  {
    id: '1557535759337282',
    title: 'Footsteps to the Sea - coastal/beach vibes',
    price: 300,
    location: 'Mt Pleasant, SC',
    availability: 'hidden',
    imageSlug: 'footsteps-to-the-sea-leslie-roberts',
    listingUrl: item('1557535759337282'),
    tags: ['Beach path', 'Coastal', 'Original'],
    note: 'This work was not shown in Leslie\'s active Marketplace listings when the catalog was checked on October 2, 2026.'
  },
  {
    id: '999306689354533',
    marketplaceId: '2137479517111526',
    title: 'Lowcountry Reflections',
    price: 200,
    previousPrice: 325,
    location: 'Mt Pleasant, SC',
    dimensions: '18 x 24',
    roomDimensions: '24 x 18',
    imageSlug: 'lowcountry-reflections-serene-marsh-landscape-leslie-roberts',
    listingUrl: item('2137479517111526'),
    tags: ['Reflection', 'Marsh', 'Original'],
    media: marketplaceMedia('lowcountry-reflections', 3)
  },
  {
    id: '26722707237432433',
    title: 'Carolina Evening Marsh',
    price: 475,
    location: 'Mt Pleasant, SC',
    availability: 'sold',
    imageSlug: 'carolina-marsh-sunset-leslie-roberts',
    listingUrl: item('26722707237432433'),
    tags: ['Evening', 'Marsh', 'Original'],
    soldNote: 'This original has sold. Similar evening marsh pieces can move quickly when they are posted.'
  },
  {
    id: '4531753170447170',
    marketplaceId: '793874783785011',
    title: 'Southern Lowlands',
    price: 200,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '20 x 16',
    imageSlug: 'southern-marsh-leslie-roberts',
    listingUrl: item('793874783785011'),
    tags: ['Lowcountry', 'Marsh', 'Framed', 'Original'],
    media: marketplaceMedia('southern-lowlands', 9, true)
  },
  {
    id: '1552247899944237',
    marketplaceId: '1492345515983882',
    title: 'Carolina Marsh',
    price: 275,
    previousPrice: 400,
    location: 'Mt Pleasant, SC',
    dimensions: '24 x 36',
    roomDimensions: '24 x 36',
    imageSlug: 'carolina-marsh-serene-marshland-and-meandering-river-leslie-roberts',
    listingUrl: item('1492345515983882'),
    tags: ['Carolina', 'Meandering water', 'Original'],
    media: marketplaceMedia('carolina-marsh', 2)
  },
  {
    id: '26356544394020042',
    marketplaceId: '4794419854111366',
    title: 'Lowcountry Salt Marsh Sundown',
    price: 325,
    previousPrice: 460,
    location: 'Huger, SC',
    dimensions: '36 x 36 x 1.5',
    roomDimensions: '36 x 36',
    imageSlug: 'salt-marsh-sundown-serene-lakeside-sunset-leslie-roberts',
    listingUrl: item('4794419854111366'),
    tags: ['Sundown', 'Marsh', 'Original'],
    media: marketplaceMedia('salt-marsh-sundown', 2)
  },
  {
    id: '1018702797521596',
    title: 'Golden Horizon',
    price: 300,
    location: 'Mt Pleasant, SC',
    availability: 'hidden',
    imageSlug: 'golden-horizon-vibrant-sunset-over-the-ocean-leslie-roberts',
    listingUrl: item('1018702797521596'),
    tags: ['Golden sky', 'Water', 'Original'],
    note: 'This work was not shown in Leslie\'s active Marketplace listings when the catalog was checked on October 2, 2026.'
  },
  {
    id: '3891978684439852',
    marketplaceId: '1408675387847524',
    title: 'Soft Southern Sky',
    price: 200,
    previousPrice: 300,
    location: 'Mt Pleasant, SC',
    dimensions: '24 x 36',
    roomDimensions: '36 x 24',
    imageSlug: 'soft-southern-sky-leslie-roberts',
    listingUrl: item('1408675387847524'),
    tags: ['Soft sky', 'Pastel', 'Framed', 'Original'],
    media: marketplaceMedia('soft-southern-sky', 4, true)
  },
  {
    id: '981438264740556',
    marketplaceId: '4627126254280839',
    title: 'Between the Banks - Lowcountry Blue',
    price: 200,
    previousPrice: 300,
    location: 'Mt Pleasant, SC',
    dimensions: '24 x 36',
    roomDimensions: '24 x 36',
    imageSlug: 'tranquil-river-landscape-at-sunset-leslie-roberts',
    listingUrl: item('4627126254280839'),
    tags: ['Blue water', 'Lowcountry', 'Framed', 'Original'],
    media: marketplaceMedia('between-the-banks-lowcountry-blue', 4, true)
  },
  {
    id: '2060350564851701',
    marketplaceId: '1365877638422615',
    title: 'Low Tide Light',
    price: 200,
    previousPrice: 300,
    location: 'Mt Pleasant, SC',
    dimensions: '24 x 36',
    roomDimensions: '36 x 24',
    imageSlug: 'serene-sunset-over-green-fields-lowtide-sunset-marsh-leslie-roberts',
    listingUrl: item('1365877638422615'),
    tags: ['Low tide', 'Large work', 'Framed', 'Original'],
    media: marketplaceMedia('low-tide-light', 4, true)
  },
  {
    id: '27510502381920173',
    marketplaceId: '874211822164779',
    title: 'Lowcountry Living',
    price: 200,
    previousPrice: 225,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '16 x 20',
    imageSlug: 'lowcountry-living-tranquil-marshland-at-sunset-leslie-roberts',
    listingUrl: item('874211822164779'),
    tags: ['Marsh sunset', 'Lowcountry', 'Framed', 'Original'],
    media: marketplaceMedia('lowcountry-living', 8, true)
  },
  {
    id: '2053881622223947',
    marketplaceId: '1347792120667764',
    title: 'Tranquil Passing',
    price: 200,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '20 x 16',
    imageSlug: 'tranquil-passing-peaceful-lakeside-with-lush-islands-leslie-roberts',
    listingUrl: item('1347792120667764'),
    tags: ['Water', 'Lush islands', 'Framed', 'Original'],
    media: marketplaceMedia('tranquil-passing', 7, true)
  },
  {
    id: '1132096796662383',
    marketplaceId: '1028532543128971',
    title: 'Lowcountry Back Woods',
    price: 275,
    previousPrice: 425,
    location: 'Mt Pleasant, SC',
    dimensions: '30 x 40',
    roomDimensions: '40 x 30',
    imageSlug: 'lowcountry-back-woods-landscape-leslie-roberts',
    listingUrl: item('1028532543128971'),
    tags: ['Woods', 'Greenery', 'Framed', 'Original'],
    media: marketplaceMedia('lowcountry-back-woods', 6, true)
  },
  {
    id: '27185304434422956',
    marketplaceId: '38896292726636247',
    title: 'Carolina Quiet',
    price: 75,
    location: 'Mt Pleasant, SC',
    dimensions: '12 x 16',
    roomDimensions: '16 x 12',
    imageSlug: 'serene-marshland-at-dusk-leslie-roberts',
    listingUrl: item('38896292726636247'),
    tags: ['Quiet marsh', 'Small work', 'Framed', 'Original'],
    media: marketplaceMedia('carolina-quiet', 3, true)
  },
  {
    id: '1803304000837901',
    marketplaceId: '892928030205035',
    title: 'Violet Marsh',
    price: 100,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '16 x 20',
    imageSlug: 'golden-marsh-light-serene-marshland-sunset-leslie-roberts',
    listingUrl: item('892928030205035'),
    tags: ['Violet sky', 'Small work', 'Framed', 'Original'],
    media: marketplaceMedia('violet-marsh', 7, true)
  },
  {
    id: '1513499150305650',
    marketplaceId: '2611277959304661',
    title: 'A Beautiful Marsh',
    price: 345,
    previousPrice: 525,
    location: 'Mt Pleasant, SC',
    dimensions: '36 x 48',
    roomDimensions: '36 x 48',
    imageSlug: 'a-beautiful-marsh-leslie-roberts',
    listingUrl: item('2611277959304661'),
    tags: ['Statement piece', 'Marsh', 'Framed', 'Original'],
    media: marketplaceMedia('a-beautiful-marsh', 5, true)
  },
  {
    id: '1460667599196084',
    marketplaceId: '1652232106652435',
    title: 'Coastal Quiet',
    price: 300,
    previousPrice: 525,
    location: 'Mt Pleasant, SC',
    dimensions: '36 x 48',
    roomDimensions: '36 x 48',
    imageSlug: 'tranquil-coastal-landscape-shoreline-serenity-leslie-roberts',
    listingUrl: item('1652232106652435'),
    tags: ['XL', 'Coastal', 'Framed', 'Original'],
    media: marketplaceMedia('coastal-quiet', 6, true)
  },
  {
    id: '981503391307582',
    marketplaceId: '1393596712229580',
    title: 'The Standing Oak',
    price: 300,
    previousPrice: 525,
    location: 'Mt Pleasant, SC',
    dimensions: '36 x 48',
    roomDimensions: '48 x 36',
    imageSlug: 'majestic-tree-in-summer-the-standing-oak-leslie-roberts',
    listingUrl: item('1393596712229580'),
    tags: ['XL', 'Oak', 'Framed', 'Original'],
    media: marketplaceMedia('standing-oak', 4, true)
  },
  {
    id: '1023671980116778',
    marketplaceId: '2996580304023807',
    title: 'Lowcountry Carolina Beauty',
    price: 300,
    previousPrice: 525,
    location: 'Mt Pleasant, SC',
    dimensions: '36 x 48',
    roomDimensions: '48 x 36',
    imageSlug: 'lowcountry-carolina-beauty-leslie-roberts',
    marketplaceImage: marketplacePhoto('lowcountry-carolina-beauty', 0, true),
    listingUrl: item('2996580304023807'),
    tags: ['XL', 'Carolina', 'Framed', 'Original'],
    media: marketplaceMedia('lowcountry-carolina-beauty', 7, true)
  },
  {
    id: '1500541894888056',
    marketplaceId: '1572025257788147',
    title: 'Original Lowcountry Marsh',
    price: 175,
    previousPrice: 200,
    location: 'Mt Pleasant, SC',
    dimensions: '24 x 36',
    roomDimensions: '36 x 24',
    imageSlug: 'marshland-landscape-leslie-roberts',
    listingUrl: item('1572025257788147'),
    tags: ['Lowcountry', 'Large work', 'Framed', 'Original'],
    media: marketplaceMedia('original-lowcountry-marsh-24x36', 5, true)
  },
  {
    id: '1692715345315357',
    title: 'Southern Marsh',
    price: 300,
    location: 'Mt Pleasant, SC',
    availability: 'hidden',
    imageSlug: 'southern-marsh-leslie-roberts',
    listingUrl: item('1692715345315357'),
    tags: ['Southern marsh', 'Dusk', 'Original'],
    note: 'This work was not shown in Leslie\'s active Marketplace listings when the catalog was checked on October 2, 2026.'
  },
  {
    id: '1294199382784513',
    marketplaceId: '1544291170509532',
    title: 'Walk to the Beach',
    price: 175,
    previousPrice: 250,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '16 x 20',
    imageSlug: 'a-walk-to-the-beach-leslie-roberts',
    listingUrl: item('1544291170509532'),
    tags: ['Beach path', 'Coastal', 'Framed', 'Original'],
    media: marketplaceMedia('walk-to-the-beach', 5, true)
  },
  {
    id: '972359105555443',
    marketplaceId: '2537856186676388',
    title: 'Dockside Dawn',
    price: 150,
    location: 'Mt Pleasant, SC',
    dimensions: '11 x 14',
    roomDimensions: '11 x 14',
    imageSlug: 'lowcountry-docks-leslie-roberts',
    listingUrl: item('2537856186676388'),
    tags: ['Dock', 'Dawn', 'Small work', 'Original'],
    note: 'Painted on a flat canvas that fits a standard 11 x 14 frame.',
    media: marketplaceMedia('dockside-dawn', 5)
  },
  {
    id: '1947717345930756',
    marketplaceId: '2337233177017119',
    title: 'A Walk on the Dock',
    price: 150,
    location: 'Huger, SC',
    dimensions: '11 x 14',
    roomDimensions: '14 x 11',
    imageSlug: 'serene-wooden-dock-at-sunset-leslie-roberts',
    listingUrl: item('2337233177017119'),
    tags: ['Dock', 'Sunset', 'Framed', 'Original'],
    media: marketplaceMedia('a-walk-on-the-dock', 8, true)
  },
  {
    id: '4648641035383706',
    title: 'Lowcountry Stillness',
    price: 1100,
    location: 'Huger, SC',
    dimensions: '48 x 36',
    roomDimensions: '48 x 36',
    imageSlug: 'lowcountry-stillness-leslie-roberts',
    marketplaceImage: marketplacePhoto('lowcountry-stillness', 0, false),
    listingUrl: item('4648641035383706'),
    tags: ['Statement piece', 'Quiet marsh', 'Wide sky', 'Original'],
    note: 'A large original built around soft clouds, reflective water, and dark saltgrass.',
    media: marketplaceMedia('lowcountry-stillness', 5)
  },
  {
    id: '1780731262936434',
    title: 'Sunset Marsh - 16 x 20',
    price: 200,
    previousPrice: 245,
    location: 'Huger, SC',
    dimensions: '16 x 20',
    roomDimensions: '16 x 20',
    imageSlug: 'sunset-marsh-16x20-leslie-roberts',
    marketplaceImage: marketplacePhoto('sunset-marsh-16x20', 0, true),
    listingUrl: item('1780731262936434'),
    tags: ['Sunset', 'Marsh', 'Framed', 'Original'],
    media: marketplaceMedia('sunset-marsh-16x20', 5, true)
  },
  {
    id: '1520889930065149',
    title: 'A View of the Ravenel',
    price: 345,
    location: 'Huger, SC',
    dimensions: '18 x 24',
    roomDimensions: '18 x 24',
    imageSlug: 'charleston-sc-coastal-town-with-bridge-leslie-roberts',
    listingUrl: item('1520889930065149'),
    tags: ['Charleston', 'Ravenel Bridge', 'Framed', 'Original'],
    media: marketplaceMedia('view-of-the-ravenel', 5, true)
  },
  {
    id: '1585169496640069',
    title: 'Sunset Marsh Diptych',
    price: 550,
    previousPrice: 820,
    location: 'Huger, SC',
    dimensions: 'Two 24 x 36 canvases',
    roomDimensions: '48 x 36',
    imageSlug: 'sunset-marsh-diptych-leslie-roberts',
    marketplaceImage: marketplacePhoto('sunset-marsh-diptych', 0, true),
    listingUrl: item('1585169496640069'),
    tags: ['Diptych', 'Sunset', 'Statement piece', 'Framed', 'Original'],
    note: 'A two-piece marsh sunset; each framed canvas is 24 x 36 inches.',
    media: marketplaceMedia('sunset-marsh-diptych', 6, true)
  },
  {
    id: '2955964304770669',
    title: 'Lighthouse and the Sea',
    price: 300,
    previousPrice: 450,
    location: 'Huger, SC',
    dimensions: '30 x 40',
    roomDimensions: '40 x 30',
    imageSlug: 'ocean-sunset-with-lighthouse-leslie-roberts',
    listingUrl: item('2955964304770669'),
    tags: ['Lighthouse', 'Sea', 'Framed', 'Original'],
    media: marketplaceMedia('lighthouse-and-the-sea', 4, true)
  },
  {
    id: '1035640489245929',
    title: 'A Cool Marsh',
    price: 200,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '20 x 16',
    imageSlug: 'a-cool-marsh-leslie-roberts',
    listingUrl: item('1035640489245929'),
    tags: ['Blue water', 'Marsh', 'Framed', 'Original'],
    media: marketplaceMedia('a-cool-marsh', 5, true)
  },
  {
    id: '2141463563082135',
    title: 'Sunset Marsh - 18 x 24',
    price: 125,
    location: 'Huger, SC',
    dimensions: '18 x 24',
    roomDimensions: '18 x 24',
    imageSlug: 'sunset-marsh-18x24-leslie-roberts',
    marketplaceImage: marketplacePhoto('sunset-marsh-18x24', 0, true),
    listingUrl: item('2141463563082135'),
    tags: ['Sunset', 'Marsh', 'Framed', 'Original'],
    media: marketplaceMedia('sunset-marsh-18x24', 3, true)
  },
  {
    id: '4378425439094933',
    title: 'Lowcountry Scene',
    price: 225,
    previousPrice: 275,
    location: 'Huger, SC',
    dimensions: '20 x 20',
    roomDimensions: '20 x 20',
    imageSlug: 'lowcountry-scene-leslie-roberts',
    marketplaceImage: marketplacePhoto('lowcountry-scene', 0, false),
    listingUrl: item('4378425439094933'),
    tags: ['Lowcountry', 'Marsh', 'Square', 'Original'],
    media: marketplaceMedia('lowcountry-scene', 6)
  },
  {
    id: '1414146040615239',
    title: 'A Simple Marsh',
    price: 125,
    previousPrice: 200,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '20 x 16',
    imageSlug: 'simple-marshland-landscape-leslie-roberts',
    listingUrl: item('1414146040615239'),
    tags: ['Marsh', 'Soft sky', 'Framed', 'Original'],
    media: marketplaceMedia('a-simple-marsh', 7, true)
  },
  {
    id: '2462980640879782',
    title: 'Cool Blue Lowcountry Marsh',
    price: 325,
    previousPrice: 500,
    location: 'Huger, SC',
    dimensions: '30 x 40',
    roomDimensions: '30 x 40',
    imageSlug: 'tranquil-marsh-landscape-leslie-roberts',
    listingUrl: item('2462980640879782'),
    tags: ['Blue water', 'Marsh', 'Gold frame', 'Original'],
    media: marketplaceMedia('cool-blue-lowcountry-marsh', 3, true)
  },
  {
    id: '1076430674921295',
    title: 'Marsh at Dusk',
    price: 200,
    previousPrice: 275,
    location: 'Mt Pleasant, SC',
    dimensions: '16 x 20',
    roomDimensions: '16 x 20',
    imageSlug: 'marshland-at-dusk-leslie-roberts',
    listingUrl: item('1076430674921295'),
    tags: ['Dusk', 'Marsh', 'Framed', 'Original'],
    media: marketplaceMedia('marsh-at-dusk', 3, true)
  },
  {
    id: '1673898670371280',
    title: 'Between the Storm',
    price: 375,
    previousPrice: 525,
    location: 'Mt Pleasant, SC',
    dimensions: '30 x 40',
    roomDimensions: '40 x 30',
    imageSlug: 'between-the-storm-leslie-roberts',
    listingUrl: item('1673898670371280'),
    tags: ['Water', 'Clouds', 'Framed', 'Original'],
    media: marketplaceMedia('between-the-storm', 4, true)
  }
];

export const marketplaceCapturedAt = 'October 2, 2026';
